import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Input,
  InputNumber,
  Table,
  Form,
  Row,
  Col,
  Steps,
  Tooltip,
  Alert,
  Modal,
  Skeleton,
  Descriptions,
} from 'antd';
import AjaxTable from '../../../components/AjaxTable';
import {
  productInfo,
  productsAssignTable,
  productsTable,
} from '../../../api/module/request_mod_payments';
import { CustomButton } from '../../../components';
import { QuestionCircleOutlined } from '@ant-design/icons';
import EllipsisModal from '../../../components/EllipsisModal';
import { catchException } from '../../../utils/helpers/helpers';
import { CustomTooltip } from '../../../components/CustomTooltip';

const InfoModal = ({ ids, payNum }) => {
  const [dataInfo, setDataInfo] = useState();

  useEffect(() => {
    async function fetchData() {
      setDataInfo();
      try {
        const res = await productInfo(ids.id, {
          tp_id: ids.tp_id,
          mp_id: ids.mp_id,
        });
        setDataInfo({
          ...res.data,
          label: res.data.tipo.startsWith('P') ? 'Producto' : 'Gasto',
        });
      } catch (error) {
        catchException(error);
      }
    }
    fetchData();
  }, [ids.id]);

  if (!dataInfo) {
    return <Skeleton loading active />;
  }

  return (
    <>
      <Descriptions
        title={`Detalle VIGENTE del Pago ${payNum} - ${dataInfo.label} # ${dataInfo.codigo}`}
        column={3}
        bordered
        layout="vertical"
      >
        <Descriptions.Item label={`${dataInfo.label} VIGENTE`} span={3}>
          {dataInfo.producto_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Fijo o variable">
          {dataInfo.tipo}
        </Descriptions.Item>
        {dataInfo.tipo.endsWith('V') ? (
          <>
            <Descriptions.Item label="Unidad VIGENTE">
              {dataInfo.nombre_unitario}
            </Descriptions.Item>
            <Descriptions.Item label="Valor unidad VIGENTE">
              ${' '}
              {dataInfo.valor_unitario
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </Descriptions.Item>
            <Descriptions.Item label="Tope máximo VIGENTE">
              {dataInfo.maximo_unitario}
            </Descriptions.Item>
            <Descriptions.Item label="Unidades para este pago VIGENTE">
              {dataInfo.uav ? dataInfo.uav.toFixed(2) : null}
            </Descriptions.Item>
            <Descriptions.Item label="Valor del producto para este pago VIGENTE">
              {dataInfo.uav
                ? `$ ${(dataInfo.uav * dataInfo.valor_unitario)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                : null}
            </Descriptions.Item>
          </>
        ) : (
          <Descriptions.Item label="Valor del producto para este pago VIGENTE">
            {dataInfo.uav === 0
              ? `$ ${dataInfo.valor_total
                  .toFixed(2)
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
              : null}
          </Descriptions.Item>
        )}
      </Descriptions>
    </>
  );
};

const ModificationPaymentForm = ({
  mod_module_c_id,
  initialValues,
  currencyText,
  onSave,
}) => {
  const [data, setData] = useState({
    numero: initialValues?.numero,
    valor: initialValues?.valor || 0,
    pp_data: (initialValues?.ctm_productos || []).reduce((pv, cv) => {
      pv[cv.producto] = cv.unidades;
      return pv;
    }, {}),
  });

  const [activeKey, setActiveKey] = useState('1');

  const [selected, setSelected] = useState(
    (initialValues?.ctm_productos || []).map((v) => v.producto),
  );

  const [pAssignData, setPAssignData] = useState();
  const [loading, setLoading] = useState(false);

  const [ivID, setIVID] = useState();

  const [aForm] = Form.useForm();
  const [form] = Form.useForm();

  useEffect(() => {
    async function fetchPAD() {
      try {
        const res = await productsAssignTable(mod_module_c_id, {
          id_list: selected,
          tp_id: initialValues.tdr_base,
          mp_id: initialValues.ctm_base,
        });
        setPAssignData(res.data);
      } catch (error) {
        catchException(error);
        setActiveKey('1');
      }
    }
    if (activeKey === '1') setPAssignData();
    if (activeKey === '2') fetchPAD();
  }, [activeKey]);

  const columnsStepA = [
    {
      title: 'No.',
      dataIndex: 'codigo',
      width: '3rem',
      align: 'center',
    },
    {
      title: 'Productos y/o entregables',
      dataIndex: 'producto_nombre',
      width: '20rem',
      render: (text) => (
        <EllipsisModal text={text} title="Producto y/o entregable" />
      ),
    },
    {
      title: (
        <>
          Tipo{' '}
          <CustomTooltip
            title={
              <>
                <ul>
                  <li>PF: Producto Fijo</li>
                  <li>GF: Gasto Fijo</li>
                  <li>PV: Producto Variable</li>
                  <li>GV: Gasto Variable</li>
                </ul>
              </>
            }
          />
        </>
      ),
      dataIndex: 'tipo',
      align: 'center',
      width: '6rem',
    },
    {
      title: 'Información VIGENTE',
      key: 'info_vigente',
      width: '6rem',
      render: (_, record) =>
        record.origen !== 3 && (
          <CustomButton
            block
            htmlType="button"
            type="default"
            text="Detalle"
            onClick={() =>
              setIVID({
                id: record.id,
                tp_id: initialValues.tdr_base,
                mp_id: initialValues.ctm_base,
              })
            }
          />
        ),
    },
    {
      title: 'Modificaciones',
      dataIndex: 'origen',
      render: (_, record) =>
        record.requiere_eliminacion
          ? 'Eliminado'
          : record.origen === 1
            ? ''
            : record.origen === 2
              ? 'Modificada'
              : 'Nueva',
      align: 'center',
      width: '8rem',
    },
    {
      title: 'Unidad MODIFICADA',
      dataIndex: 'nombre_unitario',
      width: '8rem',
    },
    {
      title: 'Valor unidad MODIFICADA',
      dataIndex: 'valor_unitario',
      width: '8rem',
      render: (t) =>
        t ? `$ ${t.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : null,
    },
    {
      title: 'Unidades por asignar',
      dataIndex: 'pago_up',
      width: '8rem',
      render: (t, record) =>
        record.variable
          ? (t + (data.pp_data[record.id] || 0)).toFixed(2)
          : null,
    },
    {
      title: 'Valor por asignar',
      key: 'valor_up',
      width: '8rem',
      render: (_, record) =>
        record.variable
          ? `$ ${(
              (record.pago_up + (data.pp_data[record.id] || 0)) *
              record.valor_unitario
            )
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          : `$ ${(
              (record.pago_up + (record.id in data.pp_data ? 1 : 0)) *
              record.valor_total
            )
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    },
  ];

  const columnsStepB = [
    ...columnsStepA.slice(0, 4),
    columnsStepA[5],
    columnsStepA[6],
    {
      title: 'Unidades VIGENTES asignadas al pago',
      dataIndex: 'uav',
      width: '8rem',
      render: (t) => (t ? t.toFixed(2) : null),
    },
    columnsStepA[7],
    {
      title: 'Unidades para este pago',
      key: 'unidades',
      render: (_, record) =>
        record.variable ? (
          <Form.Item
            name={['productos', record.id]}
            rules={[
              {
                required: true,
                message: 'Este campo es obligatorio!',
              },
              {
                validator: (_, value) => {
                  if (!value && value !== 0) {
                    return Promise.resolve();
                  } else if (isNaN(value)) {
                    return Promise.reject(
                      new Error('Debe ser un valor numérico'),
                    );
                  }
                  if (value < 0.01) {
                    return Promise.reject(new Error('Debe ser positivo'));
                  }
                  if (value > record.pago_up + (data.pp_data[record.id] || 0)) {
                    return Promise.reject(
                      new Error(
                        'El producto no tiene suficientes unidades por asignar',
                      ),
                    );
                  }
                  if (
                    value.toString().includes('.') &&
                    value.toString().split('.', 2)[1].length > 2
                  ) {
                    return Promise.reject(
                      new Error('Debe ser un valor con máximo dos decimales'),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber />
          </Form.Item>
        ) : null,
    },
    {
      title: 'Valor del producto para este pago',
      key: 'pp_value',
      render: (_, record) => (
        <>
          {record.variable ? (
            <Form.Item
              noStyle
              shouldUpdate={(pV, cV) =>
                pV.productos[record.id] !== cV.productos[record.id]
              }
            >
              {({ getFieldValue, setFieldsValue }) => {
                const v_curr =
                  (getFieldValue(['productos', record.id]) || 0) *
                  record.valor_unitario;
                setFieldsValue({
                  v_s: { ...getFieldValue('v_s'), [record.id]: v_curr },
                });
                return `$ ${v_curr
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
              }}
            </Form.Item>
          ) : (
            `$ ${record.valor_total
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          )}
        </>
      ),
    },
  ];

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: selected,
    hideSelectAll: true,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelected(selectedRowKeys);
    },
    renderCell: (_checked, record, _index, originNode) => {
      if (record.variable) {
        if (!!data.pp_data[record.id]) return originNode; // If pay already had product, check must appear
        if (record.pago_up + (data.pp_data[record.id] || 0) < 0.01) return null;
      } else {
        if (record.pago_up + (record.id in data.pp_data ? 1 : 0) < 0.01)
          return null;
      }
      return originNode;
    },
  };

  const onFinish = async (newValues) => {
    try {
      if (!('productos' in newValues)) newValues.productos = {};
      setLoading(true);
      await onSave({
        ctm_productos: selected.map((v) => ({
          producto: v,
          unidades: newValues?.productos[v],
        })),
        valor:
          Object.values(form.getFieldValue('v_s')).reduce(
            (pv, cv) => pv + cv,
            pAssignData.valor,
          ) || 0,
        numero: data.numero,
      });
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const renderTabBar = ({ activeKey, onTabClick, panes }, _DefaultTabBar) => {
    return (
      <Steps
        current={parseInt(activeKey) - 1}
        onChange={(c) => onTabClick((c + 1).toString())}
        type="navigation"
        style={{ marginBottom: '20px' }}
      >
        {panes.map((p) => (
          <Steps.Step title={p.props.tab} />
        ))}
      </Steps>
    );
  };

  return (
    <>
      <Modal
        open={!!ivID}
        onCancel={() => setIVID()}
        closable
        footer={null}
        width="80%"
      >
        <InfoModal ids={ivID} payNum={initialValues.numero} />
      </Modal>
      <Tabs
        destroyInactiveTabPane
        activeKey={activeKey}
        onChange={(v) => {
          if (v === '1') setActiveKey('1');
          if (v === '2') aForm.submit();
        }}
        size="large"
        type="card"
        renderTabBar={renderTabBar}
      >
        <Tabs.TabPane tab="Productos y/o entregables" key="1">
          <Form
            form={aForm}
            onFinish={(values) => {
              if (selected.length > 0) {
                setData((s) => ({ ...s, numero: values.numero }));
                form.setFieldValue('v_s', {}); // As the form preserves old 'rendered' data, a reset is required for deselect products
                setActiveKey('2');
              }
            }}
            layout="horizontal"
            initialValues={{ numero: data.numero }}
          >
            <Form.Item
              name="numero"
              label="Número de pago"
              rules={[
                {
                  required: true,
                  message: 'Este campo es obligatorio!',
                },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    } else if (isNaN(value)) {
                      return Promise.reject(
                        new Error('Debe ser un valor numérico'),
                      );
                    }
                    if (value < 0) {
                      return Promise.reject(new Error('Debe ser positivo'));
                    }
                    if (value.toString().includes('.')) {
                      return Promise.reject(
                        new Error('Debe ser un valor entero'),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber />
            </Form.Item>
            <AjaxTable
              columns={columnsStepA}
              rowSelection={rowSelection}
              endpoint={productsTable}
              query={`${mod_module_c_id}&filter[tipo_lista]=1`}
              pageSize={50}
              showSizeChanger
              searchInput
              tableTitle="Seleccione los productos y/o entregables asociados al pago"
            />
            <div
              style={{
                justifyContent: 'end',
                display: 'flex',
                margin: '0 2rem 0 2rem',
              }}
            >
              <CustomButton
                text="Siguiente"
                type="primary"
                size="large"
                htmlType="submit"
              />
            </div>
          </Form>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Unidades" key="2">
          {pAssignData ? (
            <Form
              form={form}
              initialValues={{
                productos: data.pp_data,
                v_s: {},
              }}
              onFinish={onFinish}
            >
              <Table
                columns={columnsStepB}
                title={() =>
                  'Digite las unidades máximas a realizar por parte del contratista para este pago:'
                }
                dataSource={pAssignData.productos}
                pagination={false}
              />
              <Row justify="space-around">
                <Col flex="25 rem">
                  <Form.Item label={<b>Número de pago</b>}>
                    <Input disabled value={data.numero} />
                  </Form.Item>
                </Col>
                <Col flex="30 rem">
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const v_s = getFieldValue('v_s');
                      const vt =
                        Object.values(v_s).reduce(
                          (pv, cv) => pv + cv,
                          pAssignData.valor,
                        ) || 0;
                      return (
                        <Row align="middle" gutter={12}>
                          <Col>
                            <Tooltip
                              title="Si este valor tiene decimales, se redondea a 2 decimales"
                              placement="topRight"
                            >
                              <b>
                                Valor total de este pago{' '}
                                <QuestionCircleOutlined />:
                              </b>
                            </Tooltip>
                          </Col>
                          <Col>
                            <Input
                              disabled
                              prefix="$"
                              value={vt
                                .toFixed(2)
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            />
                          </Col>
                          <Col>{currencyText}</Col>
                        </Row>
                      );
                    }}
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const sumValues = pAssignData.productos.reduce(
                    (sum_values, product_data) => {
                      const tp = `${product_data.es_apoyo ? 'G' : 'P'}${
                        product_data.variable ? 'V' : 'F'
                      }`;
                      const p_value = product_data.variable
                        ? (getFieldValue(['productos', product_data.id]) || 0) *
                          product_data.valor_unitario
                        : product_data.valor_total;
                      sum_values[tp] += p_value;
                      return sum_values;
                    },
                    {
                      PF: 0,
                      PV: 0,
                      GF: 0,
                      GV: 0,
                    },
                  );
                  return (
                    <Alert
                      message="Resumen de Valores que asignará a este pago por tipo de productos/gastos:"
                      description={
                        <>
                          <ul>
                            <li>
                              Valor total de productos fijos: $
                              {(sumValues.PF || 0)
                                .toFixed(2)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </li>
                            <li>
                              Valor total de gastos fijos: $
                              {(sumValues.GF || 0)
                                .toFixed(2)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </li>
                            <li>
                              Valor total de productos variables con tope: $
                              {(sumValues.PV || 0)
                                .toFixed(2)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </li>
                            <li>
                              Valor total de gastos variables con tope: $
                              {(sumValues.GV || 0)
                                .toFixed(2)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </li>
                          </ul>
                        </>
                      }
                      type="success"
                    />
                  );
                }}
              </Form.Item>

              <br />
              <div
                style={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  margin: '0 2rem 0 2rem',
                }}
              >
                <CustomButton
                  text="Atrás"
                  type="default"
                  htmlType="button"
                  className="back-button"
                  onClick={() => setActiveKey('1')}
                  size="large"
                />
                <CustomButton
                  text={`${initialValues?.id ? 'Editar' : 'Agregar'} pago`}
                  loading={loading}
                  size="large"
                  htmlType="submit"
                />
              </div>
            </Form>
          ) : (
            <Table
              title={() =>
                'Digite las unidades máximas a realizar por parte del contratista para este pago:'
              }
              loading
            />
          )}
        </Tabs.TabPane>
      </Tabs>
    </>
  );
};

export default ModificationPaymentForm;
